import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Chip,
  Fade,
  Slide,
  Fab,
  SwipeableDrawer,
  Collapse,
  ListItemButton,
  ListSubheader,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Settings,
  Logout,
  AccountCircle,
  Notifications,
  Brightness4,
  Brightness7,
  Home,
  Business,
  Security,
  Analytics,
  Assignment,
  Receipt,
  Folder,
  Person,
  Agriculture,
  MeetingRoom,
  AdminPanelSettings,
  Palette,
  Close,
  ExpandLess,
  ExpandMore,
  Apps,
  Search,
  MoreVert,
  Speed,
  Sensors,
  Wifi,
  CameraAlt,
  AccessTime,
  QrCode,
  LocationOn,
  TrendingUp,
  Storage,
  Cloud,
  Build,
  Info,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBranding, BrandLogoComponent } from './BrandingSystem';
import CoreCityGroupLogo from './CoreCityGroupLogo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0, 0, 0, 0.9)' 
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
}));

const MobileDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)' 
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    backdropFilter: 'blur(20px)',
    width: '100%',
    height: '100%',
    boxShadow: 'none',
    border: 'none',
    overflow: 'hidden',
  },
}));

const DesktopDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(0, 0, 0, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    width: 280,
    boxShadow: theme.shadows[8],
    marginTop: '64px',
    height: 'calc(100vh - 64px)',
    borderRight: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.1)'}`,
  },
}));

const StyledListItem = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: active 
    ? (theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)' 
        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)')
    : 'transparent',
  border: active 
    ? `1px solid ${theme.palette.mode === 'dark' 
        ? 'rgba(102, 126, 234, 0.3)' 
        : 'rgba(102, 126, 234, 0.2)'}`
    : '1px solid transparent',
  
  '&:hover': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.02)',
    transform: 'translateX(4px)',
  },
  
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    transition: 'all 0.3s ease',
    minWidth: 40,
  },
  
  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 500,
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
  },
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)' 
    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  padding: theme.spacing(3, 2),
  borderBottom: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'}`,
  textAlign: 'center',
}));

const CategoryHeader = styled(ListSubheader)(({ theme }) => ({
  background: 'transparent',
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '0.85rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: theme.spacing(2, 2, 1, 2),
  marginTop: theme.spacing(2),
}));

const ModernNavigation = ({ 
  onThemeToggle, 
  user, 
  onLogout,
  title = "Sistema de Gestión" 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { branding } = useBranding();
  
  // Debug: Ver qué usuario tenemos
  useEffect(() => {
    console.log('🔍 ModernNavigation - User:', user);
    console.log('👤 User Role:', user?.role);
    console.log('✅ Is Admin?:', user?.role?.includes('admin'));
  }, [user]);
  const { language, translate } = useLanguage();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const isActive = (path) => location.pathname === path;

  // Organized navigation items by category
  const navigationCategories = [
    {
      id: 'dashboard',
      title: translate('dashboard'),
      icon: <Dashboard />,
      items: [
        { text: translate('dashboard.title'), icon: <Speed />, path: '/modular-dashboard' },
        { text: translate('dashboardOverview'), icon: <Analytics />, path: '/dashboard-overview' },
      ]
    },
    {
      id: 'facility',
      title: translate('facilityManagement'),
      icon: <Business />,
      items: [
        { text: translate('employees'), icon: <People />, path: '/facility/employments' },
        { text: translate('visitors'), icon: <Person />, path: '/facility/visitors' },
        { text: translate('accessLogs'), icon: <AccessTime />, path: '/facility/access-logs' },
        { text: translate('accessValidation'), icon: <Security />, path: '/access-validation' },
        { text: translate('employeeAccess'), icon: <QrCode />, path: '/access-worker' },
        { text: '─────────', icon: null, divider: true },
        { text: '📋 Task Dashboard', icon: <Assignment />, path: '/task-dashboard' },
        { text: '📊 Kanban Board', icon: <Apps />, path: '/task-kanban' },
        { text: '💰 Budget Overview', icon: <Receipt />, path: '/budget-overview' },
        { text: '👥 Team Workload', icon: <People />, path: '/team-workload' },
        { text: '─────────', icon: null, divider: true },
        { text: '⏱️ Time Tracker', icon: <AccessTime />, path: '/time-tracker' },
        { text: '👷 Quién Trabaja', icon: <People />, path: '/schedule/who-is-working' },
        { text: '─────────', icon: null, divider: true },
        { text: '📅 Calendario', icon: <AccessTime />, path: '/schedule/calendar', adminOnly: true },
        { text: '🗺️ Mapa Empleados', icon: <LocationOn />, path: '/schedule/employee-map', adminOnly: true },
        { text: '🗄️ Database Manager', icon: <Storage />, path: '/admin/database-manager', adminOnly: true },
      ]
    },
    {
      id: 'system',
      title: translate('systemControl'),
      icon: <Settings />,
      items: [
        { text: translate('unifiControl'), icon: <Wifi />, path: '/unifi-control' },
        { text: translate('sensors'), icon: <Sensors />, path: '/sensor-status' },
        { text: translate('automation'), icon: <Build />, path: '/automation-status' },
        { text: translate('cameraManagement'), icon: <CameraAlt />, path: '/unifi-control' },
      ]
    },
    {
      id: 'cultivation',
      title: translate('cultivation'),
      icon: <Agriculture />,
      items: [
        { text: translate('harvest'), icon: <Agriculture />, path: '/harvested' },
        { text: translate('roomControl'), icon: <MeetingRoom />, path: '/room-control' },
        { text: translate('sensorAdoption'), icon: <Sensors />, path: '/sensor-adoption' },
        { text: '📊 Sensor Analytics', icon: <TrendingUp />, path: '/sensor-analytics' },
        { text: translate('roomDisplayManager'), icon: <LocationOn />, path: '/admin/room-displays' },
      ]
    },
    {
      id: 'configuration',
      title: translate('configuration'),
      icon: <Settings />,
      items: [
        { text: translate('configuration'), icon: <Settings />, path: '/project-settings' },
        { text: translate('branding'), icon: <Palette />, path: '/branding-settings' },
        { text: translate('profile'), icon: <Person />, path: '/profile' },
        { text: translate('administration'), icon: <AdminPanelSettings />, path: '/admin' },
      ]
    },
  ];

  const mobileDrawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Header */}
      <MobileHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: theme.palette.text.primary,
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <Close />
          </IconButton>
          <LanguageSwitcher />
        </Box>
        
        <CoreCityGroupLogo size={48} showText={true} />
        <Typography 
          variant="h6" 
          sx={{ 
            mt: 1,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          HARVEST AI
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ mt: 0.5, fontWeight: 500 }}
        >
          {translate('controlPanel')}
        </Typography>
      </MobileHeader>

      {/* Mobile Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {navigationCategories.map((category) => (
          <Box key={category.id}>
            <StyledListItem
              onClick={() => handleCategoryToggle(category.id)}
              sx={{ 
                justifyContent: 'space-between',
                background: 'rgba(255, 255, 255, 0.05)',
                mb: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon>{category.icon}</ListItemIcon>
                <ListItemText 
                  primary={category.title} 
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </Box>
              {expandedCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
            </StyledListItem>
            
            <Collapse in={expandedCategories[category.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {category.items.map((item, index) => {
                  // Skip admin-only items for non-admin users
                  if (item.adminOnly && !user?.role?.includes('admin')) {
                    return null;
                  }
                  
                  // Render divider
                  if (item.divider) {
                    return <Divider key={`divider-${index}`} sx={{ my: 1, mx: 2 }} />;
                  }
                  
                  // Render normal item
                  return (
                    <StyledListItem
                      key={item.text}
                      active={isActive(item.path)}
                      onClick={() => handleNavigation(item.path)}
                      sx={{ pl: 4, py: 0.5 }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                      {isActive(item.path) && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="primary" 
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </StyledListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* Mobile Footer */}
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {user?.email || 'user@example.com'}
          </Typography>
          <IconButton
            onClick={onThemeToggle}
            size="small"
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Box>
        
        <StyledListItem
          onClick={onLogout}
          sx={{ 
            background: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            '&:hover': { background: 'rgba(244, 67, 54, 0.2)' }
          }}
        >
          <ListItemIcon sx={{ color: '#f44336' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText 
            primary={translate('logout')} 
            primaryTypographyProps={{ color: '#f44336', fontWeight: 600 }}
          />
        </StyledListItem>
      </Box>
    </Box>
  );

  const desktopDrawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        mb: 1,
        pt: 3,
      }}>
        <CoreCityGroupLogo size={40} showText={true} />
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            mt: 0.5,
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          }}
        >
          {translate('controlPanel')}
        </Typography>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, px: 1 }}>
        {navigationCategories.map((category) => (
          <Box key={category.id}>
            <CategoryHeader>{category.title}</CategoryHeader>
            {category.items.map((item, index) => {
              // Skip admin-only items for non-admin users
              if (item.adminOnly && !user?.role?.includes('admin')) {
                return null;
              }
              
              // Render divider
              if (item.divider) {
                return <Divider key={`divider-${index}`} sx={{ my: 1, mx: 2 }} />;
              }
              
              // Render normal item
              return (
                <StyledListItem
                  key={item.text}
                  active={isActive(item.path)}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </StyledListItem>
              );
            })}
          </Box>
        ))}
      </List>

      {/* Theme Toggle */}
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <StyledListItem
          onClick={onThemeToggle}
          sx={{ justifyContent: 'center' }}
        >
          <ListItemIcon>
            {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </ListItemIcon>
          <ListItemText primary={theme.palette.mode === 'dark' ? translate('lightMode') : translate('darkMode')} />
        </StyledListItem>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Top App Bar */}
      <StyledAppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <BrandLogoComponent size="small" showName={false} />
            <Typography 
              variant="h6" 
              sx={{ 
                ml: 2, 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              HARVEST AI
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Theme Toggle */}
            <IconButton
              color="inherit"
              onClick={onThemeToggle}
              sx={{
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            {/* Notifications */}
            <IconButton
              color="inherit"
              sx={{
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <Badge badgeContent={3} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* Profile Menu */}
            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        onOpen={() => setDrawerOpen(true)}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {mobileDrawerContent}
      </MobileDrawer>

      {/* Desktop Drawer */}
      <DesktopDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
        open
      >
        {desktopDrawerContent}
      </DesktopDrawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.9)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); handleNavigation('/profile'); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          {translate('profile')}
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); handleNavigation('/branding-settings'); }}>
          <ListItemIcon>
            <Palette fontSize="small" />
          </ListItemIcon>
          {translate('branding')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {translate('logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default ModernNavigation; 